import React from "react";
import { Check, Info, Minus } from "lucide-react";

// Setting expectations before the user starts, rather than explaining afterwards why the
// build differs from their design. Split honestly: what is reproduced exactly by
// construction, and what is inferred because the design does not contain it.

const EXACT = [
  "Colours, type scale, spacing and corner radii — compiled into the project as tokens",
  "Every image, icon and logo, exported from your file at real paths",
  "All text, exactly as written in the design",
  "The screens you pick, and the navigation between them",
];

const INFERRED = [
  "States your design doesn't include — empty, loading, error, hover",
  "How it behaves at widths you didn't draw",
  "Motion and transitions (Figma prototype easing isn't available over the API)",
  "What the data means, so the backend is proposed for you to confirm",
];

const FigmaExpectations: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <div className="rounded-xl border border-gray-200 bg-white overflow-hidden dark:border-white/10 dark:bg-white/5">
    <div className="flex items-start gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/70 dark:border-white/10 dark:bg-white/5">
      <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5 dark:text-gray-500" />
      <p className="text-xs text-muted-foreground">
        Your design drives the build. Here's exactly what that means, so there are no surprises.
      </p>
    </div>

    <div className={`grid gap-0 ${compact ? "" : "sm:grid-cols-2"}`}>
      <div className="p-4 sm:border-r border-line">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2.5 dark:text-emerald-400">
          <Check className="h-3.5 w-3.5" />
          Matched exactly
        </h4>
        <ul className="space-y-1.5">
          {EXACT.map((item) => (
            <li key={item} className="flex gap-2 text-xs text-foreground leading-relaxed dark:text-gray-300">
              <Check className="h-3 w-3 text-emerald-600 shrink-0 mt-0.5 dark:text-emerald-400" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="p-4 border-t sm:border-t-0 border-line">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2.5 dark:text-amber-400">
          <Minus className="h-3.5 w-3.5" />
          Filled in for you
        </h4>
        <ul className="space-y-1.5">
          {INFERRED.map((item) => (
            <li key={item} className="flex gap-2 text-xs text-muted-foreground leading-relaxed dark:text-gray-400">
              <Minus className="h-3 w-3 text-amber-600 shrink-0 mt-0.5 dark:text-amber-400" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/70 dark:border-white/10 dark:bg-white/5">
      <p className="text-xs text-muted-foreground">
        Every screen gets a <strong className="text-foreground">match score</strong> against its
        Figma frame after the build, so you can see how close it landed rather than taking our word
        for it.
      </p>
    </div>
  </div>
);

export default FigmaExpectations;
